import React, { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, Cell
} from 'recharts';
import _ from 'lodash';
import { LANGUAGES, DOC_COUNTS, COLORS } from '../../constants';
import useResponsiveChart from '../../hooks/useResponsiveChart';
import CustomTooltip from '../CustomTooltip';
import CustomLegend from '../CustomLegend';
import ExplanationCard from '../ExplanationCard';

const ComprehensiveOverviewView = ({ selectedMethod, selectedModel, methodData, selectedLanguages }) => {
  const { chartHeightSmall, chartMarginCompact } = useResponsiveChart();
  const lineData = useMemo(() => {
    if (!selectedMethod || !selectedModel || !methodData[selectedMethod]?.[selectedModel]) {
      return [];
    }

    return DOC_COUNTS.map(docCount => {
      const dataPoint = { docCount };
      selectedLanguages.forEach(lang => {
        const values = methodData[selectedMethod][selectedModel][lang] || [];
        const docIndex = DOC_COUNTS.indexOf(docCount);
        dataPoint[lang] = values[docIndex] || null;
      });
      return dataPoint;
    });
  }, [selectedMethod, selectedModel, methodData, selectedLanguages]);

  const languageRanking = useMemo(() => {
    if (!selectedMethod || !selectedModel || !methodData[selectedMethod]?.[selectedModel]) {
      return [];
    }

    const modelData = methodData[selectedMethod][selectedModel];
    return selectedLanguages.map(lang => {
      const values = modelData[lang] || [];
      const validValues = values.filter(val => val !== null && val !== undefined && !isNaN(val));
      return {
        language: lang,
        maxScore: validValues.length > 0 ? _.max(validValues) : 0,
        meanScore: validValues.length > 0 ? _.mean(validValues) : 0,
        improvement: validValues.length > 1 ? _.max(validValues) - validValues[0] : 0
      };
    }).sort((a, b) => b.maxScore - a.maxScore);
  }, [selectedMethod, selectedModel, methodData, selectedLanguages]);

  const overallRankings = useMemo(() => {
    if (!methodData || Object.keys(methodData).length === 0) return {};
    
    const allCombinations = [];
    
    // Generate all method-model-language-docCount combinations
    Object.keys(methodData).forEach(method => {
      Object.keys(methodData[method]).forEach(model => {
        LANGUAGES.forEach(lang => {
          const values = methodData[method][model][lang] || [];
          values.forEach((score, docIndex) => {
            if (score !== null && score !== undefined && !isNaN(score)) {
              allCombinations.push({
                method,
                model,
                language: lang,
                docCount: DOC_COUNTS[docIndex],
                score,
                combination: `${method}-${model}-${lang}-${DOC_COUNTS[docIndex]}`
              });
            }
          });
        });
      });
    });
    
    // Sort by score to get rankings
    allCombinations.sort((a, b) => b.score - a.score);
    
    // Create rankings
    const rankings = {
      overall: allCombinations.slice(0, 10), // Top 10 overall
      byMethod: {},
      byModel: {},
      byLanguage: {},
      byDocCount: {}
    };
    
    // Group by different criteria
    ['method', 'model', 'language', 'docCount'].forEach(criteria => {
      const grouped = _.groupBy(allCombinations, criteria);
      Object.keys(grouped).forEach(key => {
        rankings[`by${criteria.charAt(0).toUpperCase() + criteria.slice(1)}`][key] = grouped[key].slice(0, 5); // Top 5 for each
      });
    });
    
    return rankings;
  }, [methodData]);

  return (
    <div className="mb-8">
      <ExplanationCard title="Comprehensive Overview Explanation">
        <p><strong>Line Chart:</strong> Shows performance trajectories for all selected languages across document counts.</p>
        <p><strong>Language Ranking:</strong> Languages ranked by maximum performance score achieved.</p>
        <p><strong>Performance Metrics:</strong> Displays max score, mean score, and improvement from baseline for each language.</p>
        <p><strong>Overall Analysis:</strong> Provides a complete view to identify best-performing languages and optimal strategies.</p>
      </ExplanationCard>

      <h3 className="text-3xl font-bold text-gray-800 mb-3 text-center">
        Comprehensive Data Overview
      </h3>
      <p className="text-lg text-gray-600 text-center mb-8">
        A complete analysis of {selectedMethod} method with {selectedModel} model.
      </p>
      
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Performance Trends */}
        <div className="chart-wrapper">
          <h4 className="text-xl font-semibold text-gray-800 mb-4 text-center">Performance Trends</h4>
          <ResponsiveContainer width="100%" height={chartHeightSmall}>
            <LineChart data={lineData} margin={chartMarginCompact}>
              <CartesianGrid strokeDasharray="4 4" stroke="#e0e7ff" />
              <XAxis
                dataKey="docCount"
                label={{ value: 'Document Count', position: 'insideBottom', offset: -10 }}
                tickLine={false}
                axisLine={{ stroke: '#cbd5e0' }}
              />
              <YAxis
                label={{ value: 'Score', angle: -90, position: 'insideLeft' }}
                tickLine={false}
                axisLine={{ stroke: '#cbd5e0' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
              {selectedLanguages.map(lang => (
                <Line
                  key={lang}
                  type="monotone"
                  dataKey={lang}
                  stroke={COLORS[lang]}
                  strokeWidth={2}
                  connectNulls={false}
                  name={lang}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Language Performance Ranking */}
        <div className="chart-wrapper">
          <h4 className="text-xl font-semibold text-gray-800 mb-4 text-center">Language Performance Ranking</h4>
          <ResponsiveContainer width="100%" height={chartHeightSmall}>
            <BarChart data={languageRanking} margin={chartMarginCompact}>
              <CartesianGrid strokeDasharray="4 4" stroke="#e0e7ff" />
              <XAxis dataKey="language" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="maxScore" name="Max Score">
                {languageRanking.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[entry.language]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="mt-8 bg-white p-6 rounded-xl shadow-lg border border-gray-200">
        <h4 className="text-xl font-semibold text-gray-800 mb-4">Performance Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {languageRanking.map((lang, index) => (
            <div key={lang.language} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900">{lang.language}</span>
                <span className="text-sm text-gray-500">#{index + 1}</span>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Max Score:</span>
                  <span className="font-medium">{lang.maxScore.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Mean Score:</span>
                  <span className="font-medium">{lang.meanScore.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Improvement:</span>
                  <span className={`font-medium ${lang.improvement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {lang.improvement >= 0 ? '+' : ''}{lang.improvement.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Overall Rankings */}
      <div className="mt-8 grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Top 10 Overall */}
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <h4 className="text-xl font-semibold text-gray-800 mb-4 text-center">🏆 Top 10 Overall Performance</h4>
          <div className="space-y-2">
            {overallRankings.overall?.slice(0, 10).map((item, index) => (
              <div key={item.combination} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                    index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-yellow-600' : 'bg-blue-500'
                  }`}>
                    {index + 1}
                  </span>
                  <div>
                    <div className="font-medium text-sm">{item.method} - {item.model}</div>
                    <div className="text-xs text-gray-600">{item.language} @ {item.docCount} docs</div>
                  </div>
                </div>
                <div className="font-bold text-lg text-gray-900">{item.score.toFixed(1)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Rankings by Category */}
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <h4 className="text-xl font-semibold text-gray-800 mb-4 text-center">📊 Top Performers by Category</h4>
          
          {/* Best by Language */}
          <div className="mb-6">
            <h5 className="font-medium text-gray-700 mb-2">🌐 Best by Language</h5>
            <div className="space-y-1">
              {Object.entries(overallRankings.byLanguage || {}).map(([lang, results]) => {
                const best = results[0];
                return (
                  <div key={lang} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="font-medium" style={{ color: COLORS[lang] }}>{lang}</span>
                    <div className="text-right text-sm">
                      <div className="font-bold">{best?.score.toFixed(1)}</div>
                      <div className="text-xs text-gray-500">{best?.method} - {best?.docCount} docs</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Best by Document Count */}
          <div className="mb-6">
            <h5 className="font-medium text-gray-700 mb-2">📄 Best by Document Count</h5>
            <div className="space-y-1">
              {Object.entries(overallRankings.byDocCount || {}).map(([docCount, results]) => {
                const best = results[0];
                return (
                  <div key={docCount} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="font-medium">{docCount} docs</span>
                    <div className="text-right text-sm">
                      <div className="font-bold">{best?.score.toFixed(1)}</div>
                      <div className="text-xs text-gray-500">{best?.method} - {best?.language}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Best by Method */}
          <div>
            <h5 className="font-medium text-gray-700 mb-2">⚡ Best by Method</h5>
            <div className="space-y-1">
              {Object.entries(overallRankings.byMethod || {}).map(([method, results]) => {
                const best = results[0];
                return (
                  <div key={method} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="font-medium text-blue-600">{method}</span>
                    <div className="text-right text-sm">
                      <div className="font-bold">{best?.score.toFixed(1)}</div>
                      <div className="text-xs text-gray-500">{best?.language} @ {best?.docCount} docs</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComprehensiveOverviewView;